import { api, getAccessToken } from '@/lib/api';

/**
 * FCM native push bridge for the Capacitor Android app.
 * The app loads the live website remotely; Capacitor injects the native
 * PushNotifications plugin into window.Capacitor.Plugins, so no npm
 * dependency is needed in the web bundle. Browser usage is a no-op.
 */

interface CapacitorPluginHandle {
  register(): Promise<void>;
  requestPermissions?(): Promise<{ receive?: string }>;
  addListener(event: string, cb: (data: any) => void): Promise<{ remove(): void }>;
  removeAllListeners?(): Promise<void>;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  Plugins?: Record<string, CapacitorPluginHandle | undefined>;
}

declare global {
  interface Window {
    Capacitor?: CapacitorGlobal;
  }
}

let registeredToken: string | null = null;

/** True when running inside the Capacitor native app. */
export function isAppEnvironment(): boolean {
  return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
}

function plugin(): CapacitorPluginHandle | undefined {
  return window.Capacitor?.Plugins?.PushNotifications;
}

/**
 * Register this app install with FCM and persist the token server-side for
 * the current signed-in user. Safe to call repeatedly — token changes only
 * re-register when the FCM token actually changed.
 */
export async function ensureAppPush(): Promise<{ ok: boolean; reason: string }> {
  if (typeof window === 'undefined') return { ok: false, reason: 'unsupported' };
  const PN = plugin();
  if (!isAppEnvironment() || !PN) return { ok: false, reason: 'unsupported' };
  if (!getAccessToken()) return { ok: false, reason: 'guest' };

  try {
    if (PN.requestPermissions) {
      const perm = await PN.requestPermissions();
      if (perm.receive === 'denied') return { ok: false, reason: 'denied' };
    }
    PN.addListener('registration', async ({ value }: { value: string }) => {
      if (!value || value === registeredToken) return;
      registeredToken = value;
      try {
        await api.post('/push-subscriptions/fcm/register', { fcmToken: value, platform: 'android' });
      } catch {
        /* token persisted next launch */
      }
    });
    PN.addListener('registrationError', ({ error }: { error: string }) => {
      console.warn('[fcm] registration error', error);
    });
    PN.addListener(
      'pushNotificationActionPerformed',
      ({ notification }: { notification: { data?: { url?: string } } }) => {
        const url = notification.data?.url;
        if (url && url.startsWith('/') && !url.startsWith('//')) {
          window.location.assign(url);
        }
      },
    );
    await PN.register();
    return { ok: true, reason: 'granted' };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** Forget the current device token (logout). */
export async function disableAppPush(): Promise<void> {
  if (typeof window === 'undefined') return;
  const PN = plugin();
  if (!isAppEnvironment() || !PN) return;
  try {
    await PN.removeAllListeners?.();
  } catch {
    /* best-effort */
  }
  if (registeredToken && getAccessToken()) {
    try {
      await api.delete(`/push-subscriptions/fcm?fcmToken=${encodeURIComponent(registeredToken)}`);
    } catch {
      /* best-effort */
    }
    registeredToken = null;
  }
}
